import React from 'react';
import JobCategoryPage from './JobCategoryPage';
import { Building } from 'lucide-react';

const CommunitySocialWorkerJobs = () => {
  const contentSections = [
    {
      title: "What is a Community Social Worker?",
      content: (
        <p>
          Community social workers are licensed professionals who serve populations through government agencies, nonprofits, and community-based organizations. Working in settings like Child Protective Services (CPS), Adult Protective Services, the VA system, county agencies, and nonprofit organizations, these professionals advocate for vulnerable populations while connecting individuals and families with essential resources and support services.
        </p>
      )
    },
    {
      title: "Community Social Worker Job Responsibilities",
      content: (
        <p>
          Community social workers address a wide range of social needs, from child welfare to aging services. Their work involves direct client services, case management, advocacy, and community outreach.
        </p>
      ),
      list: [
        "Conduct investigations and assessments for protective services",
        "Develop and implement case plans for families and individuals",
        "Connect clients with community resources and support services",
        "Provide crisis intervention and safety planning",
        "Advocate for client rights and access to services",
        "Collaborate with law enforcement, courts, and other agencies",
        "Document case activities and maintain compliance with regulations",
        "Participate in multidisciplinary team meetings",
        "Provide community education and outreach programs"
      ]
    },
    {
      title: "Government and Community Agency Settings",
      content: (
        <p>
          Community social workers find employment across numerous public and nonprofit settings, each serving different populations with unique needs.
        </p>
      ),
      list: [
        "Child Protective Services (CPS)",
        "Adult Protective Services (APS)",
        "Department of Veterans Affairs (VA)",
        "State health and human services departments",
        "County social services agencies",
        "Nonprofit community organizations",
        "Refugee and immigration services",
        "Housing and homeless services programs"
      ]
    },
    {
      title: "Required Qualifications and Licenses",
      content: (
        <p>
          Community and government social work positions have varying requirements depending on the agency and role. Many entry-level positions accept LBSW credentials, while supervisory and clinical roles typically require MSW and LCSW licensure. Government positions often include additional background check requirements and may offer civil service benefits.
        </p>
      ),
      list: [
        "Bachelor's or Master's of Social Work from a CSWE-accredited program",
        "State social work licensure (LBSW, LMSW, or LCSW)",
        "Ability to pass extensive background checks",
        "Valid driver's license for field work",
        "Knowledge of relevant laws and regulations",
        "Strong crisis intervention and de-escalation skills",
        "Cultural competency and language skills often preferred"
      ]
    },
    {
      title: "Salary and Benefits",
      content: (
        <p>
          Government social work positions offer competitive salaries typically ranging from $40,000 to $70,000+, with additional benefits including pension plans, health insurance, paid time off, and job security. Many government agencies also offer student loan forgiveness programs for public service employees. Nonprofit salaries vary but often include meaningful work-life balance and mission-driven environments. Advancement opportunities include supervisor, program manager, and director positions.
        </p>
      )
    },
    {
      title: "Why Choose Community Social Work?",
      content: (
        <p>
          Community social work offers the opportunity to create systemic change and serve society's most vulnerable populations. For those passionate about social justice, advocacy, and public service, community social work provides meaningful career paths with job stability and comprehensive benefits. The diverse settings and populations allow social workers to find their niche while making a tangible difference in their communities.
        </p>
      )
    }
  ];

  const relatedCategories = [
    { name: "Clinical Social Worker Jobs", href: "/clinical-social-worker-jobs" },
    { name: "School Social Worker Jobs", href: "/school-social-worker-jobs" },
    { name: "Hospital Social Worker Jobs", href: "/hospital-social-worker-jobs" },
    { name: "Medical Social Worker Jobs", href: "/medical-social-worker-jobs" },
  ];

  return (
    <JobCategoryPage
      category="community-social-worker-jobs"
      title="Community & Government Social Worker Jobs"
      metaTitle="Government Social Worker Jobs | CPS, VA, Community Agency Positions | INeedASocialWorker"
      metaDescription="Find community and government social worker jobs at CPS, VA, county agencies, and nonprofits. LBSW, LMSW, and LCSW positions with benefits. Apply with SW Express Apply™."
      heroTitle="Community & Government Social Worker Jobs"
      heroSubtitle="Make a difference in your community with social work positions at government agencies, nonprofits, and community organizations."
      searchKeywords={["government social worker", "CPS social worker", "VA social worker", "community social worker", "county social worker", "nonprofit social worker", "child welfare social worker"]}
      contentSections={contentSections}
      relatedCategories={relatedCategories}
      iconComponent={Building}
    />
  );
};

export default CommunitySocialWorkerJobs;
